import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { Link, useParams } from 'react-router-dom';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import PageName from '../../components/PageName';
import Button from '../../components/Button';
import CircleImage from '../../components/CircleImage';
import DisplayDateTime from '../../components/DisplayDateTime';
import ScrollToTopButton from '../../components/ScrollToTopButton';
import LoadingSpinner from '../../components/LoadingSpinner';
import GlobalModal from '../../components/GlobalModal';
import AcceptModal from './AcceptModal';
import AcknowledgeModal from './AcknowledgeModal';

// ICONS
import { ReactComponent as DownloadIcon } from '../../assets/svg/download.svg';
import { ReactComponent as TickIcon } from '../../assets/svg/tick.svg';
import { ReactComponent as TickSuccessIcon } from '../../assets/svg/tick-success.svg';
import { ReactComponent as EnvelopeIcon } from '../../assets/svg/envelope-icon.svg';

// TYPES
import { IncidentArchiveEditCommand } from '../../api/models';
import { RouteParams } from '../../Interfaces';

const IncidentReportsDetail: React.FC = () => {
	// Check if Organisation is active
	const isOrganisationActive = useAppSelector((state) => state.user.isOrganisationActive);

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// get ID from params
	const params = useParams<RouteParams>();
	const incidentId = params.id;

	// State to control modals
	const [showAcceptModal, setShowAcceptModal] = React.useState<boolean>(false);
	const [showAcknowledgeModal, setShowAcknowledgeModal] = React.useState<boolean>(false);
	const [modalId, setModalId] = React.useState<string>('');

	const openModal = (id: string) => {
		setModalId(id);

		setShowAcceptModal(true);
	};

	// Set up data state and initial data
	const initialIncidentReportsDetailData: IncidentArchiveEditCommand = {
		id: undefined,
		title: undefined,
		creator: undefined,
		type: undefined,
		typeDescription: undefined,
		occurrence: undefined,
		location: undefined,
		impacted: undefined,
		wasWitnessedFirstHand: undefined,
		wasAuthorityAlerted: undefined,
		notifiedAuthorities: undefined,
		notified: undefined,
		description: undefined,
		actions: undefined,
		summaryUrl: undefined,
		attachments: [],
		contacts: []
	};

	const [incidentReportsDetailData, setIncidentReportsDetailData] = React.useState<IncidentArchiveEditCommand>(
		initialIncidentReportsDetailData
	);

	const getIncidentReportsDetailsDataArchive = async () => {
		setIsLoading(true);

		const response = await apibridge.getIncidentReportsDetailsDataArchive(incidentId);

		response.result = response.result || initialIncidentReportsDetailData;

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);

			setIncidentReportsDetailData(response.result);
		}
	};

	React.useEffect(() => {
		getIncidentReportsDetailsDataArchive();
	}, []);

	const {
		title,
		creator,
		id,
		identifier,
		type,
		typeDescription,
		occurrence,
		location,
		impacted,
		wasWitnessedFirstHand,
		wasAuthorityAlerted,
		notifiedAuthorities,
		notified,
		description,
		actions,
		attachments,
		contacts,
		summaryUrl,
		canEdit,
		canAcknowledge,
		isAcknowledged,
		allAcknowledged
	} = incidentReportsDetailData;

	return (
		<div className="page-general-layout">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								<PageName title={title} showBack={true} />

								<div className="complaints-detail-general-info">
									<div className="general-info-left">
										<div className="info-col">
											<div className="info-title">Submitted by</div>

											<div className="info-detail">{creator}</div>
										</div>

										<div className="info-col">
											<div className="info-title">Incident ID</div>

											<div className="info-detail">{identifier}</div>
										</div>

										<div className="info-col">
											<div className="info-title">Category</div>

											<div className="info-detail">{typeDescription}</div>
										</div>
									</div>

									<div className="general-info-right">
										{isOrganisationActive && canEdit && (
											<Link className="btn-primary resolve-btn" to={`/incident-submit-archive/${id}`}>
												Edit
											</Link>
										)}

										{isOrganisationActive && canAcknowledge && (
											<Button
												className="btn-primary resolve-btn mleft-10"
												onClick={() => setShowAcknowledgeModal(true)}
												disabled={isAcknowledged}
											>
												Acknowledge
											</Button>
										)}
									</div>
								</div>

								<div className="two-col-layout">
									<div className="complaints-detail">
										<div className="detail-block">
											<h2 className="detail-heading">Date and time of incident</h2>

											<time className="detail-info">
												<DisplayDateTime>{occurrence}</DisplayDateTime>
											</time>
										</div>

										<div className="detail-block">
											<h2 className="detail-heading">Incident type</h2>

											<p className="detail-info">{typeDescription}</p>
										</div>

										<div className="detail-block">
											<h2 className="detail-heading">Where did the incident happen?</h2>

											<p className="detail-info">{location}</p>
										</div>

										<div className="detail-block">
											<h2 className="detail-heading">Who was involved in this incident?</h2>

											<p className="detail-info">{impacted}</p>
										</div>

										<div className="detail-block">
											<h2 className="detail-heading">Did you witness the incident first hand?</h2>

											<p className="detail-info">{wasWitnessedFirstHand ? 'Yes' : 'No'}</p>
										</div>

										<div className="detail-block">
											<h2 className="detail-heading">Was an authority alerted?</h2>

											<p className="detail-info">{wasAuthorityAlerted ? 'Yes' : 'No'}</p>
										</div>

										{notifiedAuthorities && (
											<div className="detail-block">
												<h2 className="detail-heading">
													Name, organisation, position and contact details of notified authority
												</h2>

												<p className="detail-info">{notifiedAuthorities}</p>
											</div>
										)}

										<div className="detail-block">
											<h2 className="detail-heading">Date and time authority was notified</h2>

											<time className="detail-info">
												<DisplayDateTime>{notified}</DisplayDateTime>
											</time>
										</div>

										<div className="detail-block">
											<h2 className="detail-heading">Incident description</h2>

											<p className="detail-info">{description}</p>
										</div>

										<div className="detail-block">
											<h2 className="detail-heading">What were the actions taken?</h2>

											<p className="detail-info">{actions}</p>
										</div>

										{attachments && attachments.length > 0 && (
											<div className="detail-block">
												<h2 className="detail-heading">Attachments</h2>

												<ul>
													{attachments.map((item, index) => {
														return (
															<li key={index}>
																{item && item.link && (
																	<a
																		className="attachments-download-link"
																		href={item.link}
																		download={item.displayFileName}
																	>
																		<span className="link-name">{item.displayFileName}</span>

																		<span className="link-text d-none d-lg-block">Download</span>

																		<DownloadIcon className="link-icon d-block d-lg-none" />
																	</a>
																)}
															</li>
														);
													})}
												</ul>
											</div>
										)}

										<a className="btn btn-secondary mtop-40 download-btn" href={`${summaryUrl}`} download>
											<DownloadIcon className="link-icon" />
											Download Incident Summary
										</a>
									</div>

									<div className="notified-contacts-container">
										<div className="notified-contacts-box">
											<div className="notified-contact-header">
												<h2>Notified contacts</h2>

												<p className={`${allAcknowledged ? 'purple-text' : null}`}>Acknowledged</p>
											</div>

											<ul>
												{contacts &&
													contacts.length > 0 &&
													contacts.map((item, index) => {
														return (
															<li key={index} className="row notified-contact-row">
																{item.isExternal ? (
																	<>
																		<div className="col-2 contact-profile">
																			<EnvelopeIcon />
																		</div>

																		<div className="col-8 contact-info">
																			<p className="email">{item.email}</p>
																			{isOrganisationActive && item.isAcknowledged && item.canAcknowledge && (
																				<div className="button-group">
																					<a
																						href={item.acknowledgmentDocument}
																						className="btn-text pdl-0"
																						download="Acknowledge Document"
																					>
																						View
																					</a>
																					/
																					<Button
																						className="btn-text"
																						onClick={() => openModal(item.recipientId || '')}
																					>
																						Update Document
																					</Button>
																				</div>
																			)}
																		</div>

																		<div className="col-2 contact-action">
																			{item.isAcknowledged ? (
																				<TickSuccessIcon className="tick-icon" />
																			) : isOrganisationActive && item.canAcknowledge ? (
																				<Button className="btn-text" onClick={() => openModal(item.recipientId || '')}>
																					Accept
																				</Button>
																			) : null}
																		</div>
																	</>
																) : (
																	<>
																		<div className="col-2 contact-profile">
																			<CircleImage
																				size={40}
																				firstName={item.firstName}
																				lastName={item.lastName}
																				src={item.profileImage}
																			/>
																		</div>

																		<div className="col-8 contact-info">
																			<p className="name">
																				{item.firstName} {item.lastName} {item.isYou ? <strong>(You)</strong> : null}
																			</p>

																			<p className="role">{item.role}</p>
																		</div>

																		<div className="col-2 contact-action">
																			{item.isAcknowledged ? (
																				<TickSuccessIcon className="tick-icon" />
																			) : (
																				<TickIcon className="tick-icon" />
																			)}
																		</div>
																	</>
																)}
															</li>
														);
													})}
											</ul>

											<div className="notified-contact-guide">
												<strong>Please note: </strong>
												Manually accepting a report on behalf of a recipient requires the uploading of a signed
												document.
											</div>
										</div>
									</div>
								</div>

								<GlobalModal isOpen={showAcceptModal} setIsOpen={setShowAcceptModal} titleText="Accept on behalf of...">
									<AcceptModal
										recipientId={modalId}
										getIncidentReportsDetailsDataArchive={getIncidentReportsDetailsDataArchive}
									/>
								</GlobalModal>

								<GlobalModal
									isOpen={showAcknowledgeModal}
									setIsOpen={setShowAcknowledgeModal}
									titleText="Acknowledge incident"
								>
									<AcknowledgeModal
										getIncidentReportsDetailsDataArchive={getIncidentReportsDetailsDataArchive}
										incidentId={incidentId}
										title={title}
									/>
								</GlobalModal>

								<ScrollToTopButton offsetValue={100} />
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default IncidentReportsDetail;
